import { ErrorCodes } from '@agroone/entities'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { EMPTY, Observable, of, throwError, timer } from 'rxjs'
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators'
import { ConnectionError, HttpError } from '../models/errors'
import { AuthService, LoggerService } from '../services'

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private logger = inject(LoggerService)
  private router = inject(Router)
  private authService = inject(AuthService)

  private readonly maxRetryAttempts: number = 1

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const message: string = error.error?.message ?? error.message
        const rError: HttpError | ConnectionError = navigator.onLine
          ? new HttpError(message)
          : new ConnectionError(message)

        if (rError instanceof HttpError) {
          this.logger.debug('Catched error', request.url, location.origin)

          // Check API URLs
          if (this.isApiUrl(request.url)) {
            // Handle authentication errors and redirections
            if (this._isErrorAnAuthError(error)) {
              return this.handleAuthErrors(error).pipe(switchMap((v) => (v ? this.retryRequest(request, next) : EMPTY)))
            }

            // Retry mechanism for GET requests
            if (request.method === 'GET') {
              this.logger.debug('Retrying GET request')
              return this.retryRequest(request, next)
            }
          }

          rError.status = error.status
        }

        // If no specific handling, throw the error
        throw rError
      }),
      // retry request or refresh token may throw an new error
      catchError((error) => {
        throw error
      }),
    )
  }

  // Utility function to check if the URL belongs to the API
  private isApiUrl(url: string): boolean {
    return url.startsWith('http://localhost:3000') || url.startsWith('https://api')
  }

  private _isErrorAnAuthError(error: HttpErrorResponse): boolean {
    return (
      error.error?.message === ErrorCodes.REFRESH_TOKEN_EXPIRED ||
      error.error?.message === ErrorCodes.MISSING_AUTH ||
      error.error?.message === ErrorCodes.INVALID_TOKEN ||
      error.error?.message === ErrorCodes.MISSING_PERMISSION_MODULE_ACCESS ||
      error.error?.message === ErrorCodes.USER_INACTIVE ||
      error.error?.message === ErrorCodes.TOKEN_ALREADY_USED ||
      error.error?.message === ErrorCodes.TOKENS_EXPIRED
    )
  }

  // Function to handle authentication-related errors
  private handleAuthErrors(error: HttpErrorResponse): Observable<boolean> {
    if (error.error?.message === ErrorCodes.USER_INACTIVE || error.error?.message === ErrorCodes.TOKEN_ALREADY_USED) {
      this.router.navigate(['/access-denied'], { state: { message: error.error.message } })
      return of(true)
    }

    if (error.error?.message === ErrorCodes.TOKENS_EXPIRED) {
      this.logger.debug('Refreshing tokens')

      return this.authService.refreshTokens().pipe(
        tap(() => {
          this.logger.debug('Token refreshed successfully')
        }),
        catchError((refreshError) => {
          this.logger.error('Token refresh failed', refreshError)
          this.authService.redirectToLogin()
          return throwError(() => refreshError)
        }),
        map(() => true),
      )
    }

    if (
      error.error?.message === ErrorCodes.REFRESH_TOKEN_EXPIRED ||
      error.error?.message === ErrorCodes.MISSING_AUTH ||
      error.error?.message === ErrorCodes.INVALID_TOKEN ||
      error.error?.message === ErrorCodes.MISSING_PERMISSION_MODULE_ACCESS
    ) {
      this.authService.redirectToLogin()
      return of(false)
    }

    return of(true)
  }

  private retryRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retry({
        count: this.maxRetryAttempts,
        delay: (error: unknown, retryCount: number) => {
          if (retryCount >= this.maxRetryAttempts) {
            throw error // Stop retrying after maxRetryAttempts
          }
          this.logger.debug(`Retrying... attempt #${retryCount + 1}`)
          return timer((retryCount + 1) * 1000) // Delay increasing with each retry
        },
        resetOnSuccess: true,
      }),
      catchError((retryError: HttpErrorResponse) => {
        this.logger.debug('Final retry failed', retryError)
        throw retryError
      }),
    )
  }
}
